/**
 * Admin · Village Control Centre (iter 31.88, 27 May 2026)
 *
 * Single page Six can scan in the morning. Aggregates every admin /
 * maintenance operation that previously hung off discrete pages, plus
 * acts as the home for future ones.
 *
 * Operations in this version:
 *   - Health snapshot: balance, station tracks landed, latest comms
 *   - Welcome credits claim  (mirror of iter 31.85 button on /billing)
 *   - Canonical comms normalise  (mirror of iter 31.87 panel on /comms)
 *   - Radio brand normalise  (NEW iter 31.88)
 *
 * Owner-only by route guard. Every action is idempotent server-side,
 * supports dry-run where applicable, and reports a structured result
 * inline. Per-page buttons stay where they were — this is an additive
 * canonical home, not a replacement.
 */
import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, ShieldAlert, Gift, RefreshCw, Radio as RadioIcon, Activity, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import BuildBadge from "@/components/BuildBadge";
import SiteSnapshotPanel from "@/components/SiteSnapshotPanel";
import KeysManifestPanel from "@/components/KeysManifestPanel";  // iter 31.158
import AgentRegistryPanel from "@/components/AgentRegistryPanel";  // iter 31.303 — D1
import ContentBlocksPanel from "@/components/ContentBlocksPanel";  // iter 31.306 — D2
import MultiAgentTokensPanel from "@/components/MultiAgentTokensPanel";  // iter 31.294 + 295
import RadioPoolPanel from "@/components/RadioPoolPanel";  // iter 31.297
import StationPublisherPanel from "@/components/StationPublisherPanel";  // iter 31.300
import MurekaExportPanel from "@/components/MurekaExportPanel";  // iter 31.305 — D4
import LinkedInContactsPanel from "@/components/LinkedInContactsPanel";  // iter 31.309
import AddressesPanel from "@/components/AddressesPanel";  // iter 31.160
import EasterEggSlot from "@/components/EasterEggSlot";  // iter 31.215
import MCPStatusPanel from "@/components/MCPStatusPanel";  // iter 31.217
import PairingDiagnostic from "@/components/PairingDiagnostic";  // iter 31.239
import MurekaLibraryTile from "@/components/MurekaLibraryTile";  // iter 31.254
import TestModeStatusBanner from "@/components/TestModeStatusBanner";  // iter 31.211b

// Match the StampHeader convention used in Comms.jsx etc.
function StampHeader({ kicker, title, sub }) {
    return (
        <div className="mb-6">
            <div className="label-typed tw-prefix">{kicker}</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-stamp-red mt-1">{title}</h1>
            {sub ? <p className="font-body text-sm text-ink-soft mt-2 max-w-3xl">{sub}</p> : null}
        </div>
    );
}

function HealthRow({ label, value, hint }) {
    return (
        <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-ink/15 last:border-b-0">
            <div>
                <div className="font-heading uppercase tracking-[0.18em] text-[10px] text-ink-soft">{label}</div>
                {hint ? <div className="font-body text-[10px] text-ink-faint mt-0.5">{hint}</div> : null}
            </div>
            <div className="font-heading text-stamp-red text-lg" data-testid={`admin-health-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                {value}
            </div>
        </div>
    );
}

function ResultPanel({ result }) {
    if (!result) return null;
    if (result.error) {
        return (
            <div className="mt-3 label-typed text-[11px] leading-relaxed border-l-2 border-stamp-red pl-3 text-stamp-red">
                Error: {result.error}
            </div>
        );
    }
    return (
        <div
            className="mt-3 label-typed text-[11px] leading-relaxed border-l-2 border-stamp-red pl-3"
            data-testid={result.testid}
        >
            {result.summary && <div>{result.summary}</div>}
            {result.lines?.map((l, i) => <div key={i} className="mt-1">{l}</div>)}
            {result.samples?.length > 0 && (
                <div className="mt-2 opacity-80">
                    Samples:{" "}
                    {result.samples.slice(0, 4).map((s, i) => (
                        <span key={i} className="mr-2">
                            <code className="text-ink">{s.from ?? "(null)"}</code> → <code className="text-ink">{s.to}</code>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}


function HealthPanel() {
    const [data, setData] = useState(null);

    const load = async () => {
        try {
            const [balance, station, comms] = await Promise.all([
                api.get("/credits/balance").catch(() => null),
                api.get("/radio/station/catalogue").catch(() => null),
                api.get("/comms/outbound?limit=1&target_agent=all").catch(() => null),
            ]);
            setData({
                balance: balance?.data?.balance ?? "—",
                catalogue: station?.data?.count ?? "—",
                lastOutboundTs: comms?.data?.items?.[0]?.filed_at?.slice(0, 19) ?? "—",
                lastOutboundTarget: comms?.data?.items?.[0]?.target_agent ?? "—",
            });
        } catch (e) {
            // ignore — keep prior state
        }
    };

    useEffect(() => { load(); }, []);

    return (
        <div className="paper-card border-2 border-ink/30 p-5 mb-6" data-testid="admin-health">
            <div className="flex items-baseline justify-between gap-3 mb-3">
                <h2 className="font-heading text-sm uppercase tracking-[0.22em] text-ink">
                    Status board
                </h2>
                <button
                    type="button"
                    onClick={load}
                    className="font-heading uppercase text-[10px] tracking-[0.22em] text-ink-soft hover:text-stamp-red inline-flex items-center gap-1"
                    data-testid="admin-health-refresh"
                >
                    <RefreshCw className="w-3 h-3" /> Refresh
                </button>
            </div>
            {!data ? (
                <div className="flex items-center gap-2 text-ink-soft text-xs">
                    <Loader2 className="w-3 h-3 animate-spin" /> Reading…
                </div>
            ) : (
                <div>
                    <HealthRow label="Balance" value={`${data.balance} credits`} hint="Live, top-right pill" />
                    <HealthRow label="Station catalogue" value={`${data.catalogue} prompts`} hint="Iter 31.78 — currently 120 across 4 brands" />
                    <HealthRow label="Latest outbound" value={data.lastOutboundTarget} hint={data.lastOutboundTs} />
                </div>
            )}
        </div>
    );
}


function WelcomeCreditsPanel() {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const claim = async () => {
        setBusy(true); setResult(null);
        try {
            const { data } = await api.post("/credits/claim-welcome");
            // Iter 31.90 — auto-reconcile from the ledger if the
            // cache says zero on a duplicate claim (production
            // desync seen on Six's emes.m.andrews@gmail.com).
            let balance = data.balance;
            let extraLine = null;
            if (data?.duplicate && (data?.balance ?? 0) === 0) {
                try {
                    const { data: rec } = await api.post("/credits/reconcile-balance");
                    balance = rec.new_balance;
                    if (rec.repaired) {
                        extraLine = `Cache desync repaired · summed ${rec.ledger_rows_summed} ledger rows · delta ${rec.delta > 0 ? "+" : ""}${rec.delta}`;
                    }
                } catch (e2) {
                    extraLine = `Reconcile failed: ${e2?.response?.data?.detail || e2.message}`;
                }
            }
            window.dispatchEvent(new Event("shed:credits-changed"));
            const lines = [`Balance: ${balance}`];
            if (extraLine) lines.push(extraLine);
            setResult({
                testid: "admin-welcome-result",
                summary: data.duplicate
                    ? "Already claimed — balance current."
                    : "Claimed 100 credits.",
                lines,
            });
        } catch (e) {
            setResult({ error: e?.response?.data?.detail || e.message });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="paper-card border border-dashed border-ink/30 p-4 mb-4" data-testid="admin-welcome">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink inline-flex items-center gap-2">
                    <Gift className="w-3.5 h-3.5" /> Welcome credits
                </h3>
                <span className="label-typed text-[10px]">100 credits · idempotent</span>
            </div>
            <p className="font-body text-xs text-ink-soft mb-3 max-w-3xl">
                Safety net for operators whose auto-grant on first sign-in didn't fire
                (e.g. Google-auth users from before iter 31.75). Server-side idempotent
                on the welcome:&lt;user_id&gt; ref_id — calling this on a credited account
                returns <code className="text-ink">duplicate=true</code> and leaves your balance alone.
            </p>
            <button
                onClick={claim}
                disabled={busy}
                data-testid="admin-welcome-btn"
                className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-40 disabled:cursor-not-allowed"
            >
                {busy ? "Claiming…" : "Claim 100 welcome credits"}
            </button>
            <ResultPanel result={result} />
        </div>
    );
}


function CommsNormalisePanel() {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const run = async (dryRun) => {
        setBusy(true); setResult(null);
        try {
            const url = `/comms/normalise-existing-rows${dryRun ? "?dry_run=true" : ""}`;
            const { data } = await api.post(url);
            setResult({
                testid: "admin-comms-result",
                summary: `${dryRun ? "Dry-run" : "Run"} complete${data.dry_run ? " (no writes)" : ""}.`,
                lines: [
                    `Outbound: ${data.outbound.scanned} scanned · ${data.outbound.updated} ${dryRun ? "would update" : "updated"} · ${data.outbound.skipped} skipped`,
                    `Inbound: ${data.inbound.scanned} scanned · ${data.inbound.updated} ${dryRun ? "would update" : "updated"} · ${data.inbound.skipped} skipped`,
                ],
                samples: [...(data.outbound.samples || []), ...(data.inbound.samples || [])],
            });
        } catch (e) {
            setResult({ error: e?.response?.data?.detail || e.message });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="paper-card border border-dashed border-ink/30 p-4 mb-4" data-testid="admin-comms">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink inline-flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Comms · canonical normalisation
                </h3>
                <span className="label-typed text-[10px]">owner only · idempotent</span>
            </div>
            <p className="font-body text-xs text-ink-soft mb-3 max-w-3xl">
                Rewrites legacy <code className="text-ink">target_agent</code> /{" "}
                <code className="text-ink">source_agent</code> values
                (<code className="text-ink">claude&nbsp;(Anthropic)</code>,{" "}
                <code className="text-ink">two</code>,{" "}
                <code className="text-ink">agent&nbsp;3</code>,{" "}
                <code className="text-ink">Earnie</code>) to the Village name. Original
                value preserved in <code className="text-ink">*_pre_normalise</code>.
            </p>
            <div className="flex flex-wrap items-center gap-2">
                <button
                    onClick={() => run(true)}
                    disabled={busy}
                    data-testid="admin-comms-dryrun"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Dry-run
                </button>
                <button
                    onClick={() => {
                        if (window.confirm("Rewrite legacy comms target/source agent values to canonical Village names?\n\nOriginal values are preserved in *_pre_normalise. Idempotent.")) {
                            run(false);
                        }
                    }}
                    disabled={busy}
                    data-testid="admin-comms-run"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {busy ? "Working…" : "Normalise now"}
                </button>
            </div>
            <ResultPanel result={result} />
        </div>
    );
}


function RadioBrandPanel() {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const run = async (dryRun) => {
        setBusy(true); setResult(null);
        try {
            const url = `/radio/brand-normalise${dryRun ? "?dry_run=true" : ""}`;
            const { data } = await api.post(url);
            setResult({
                testid: "admin-radio-result",
                summary: `${dryRun ? "Dry-run" : "Run"} complete${data.dry_run ? " (no writes)" : ""}.`,
                lines: [
                    `${data.scanned} tracks scanned · ${data.updated} ${dryRun ? "would update" : "updated"} · ${data.skipped_canonical} already canonical · ${data.skipped_unclassifiable} unclassifiable`,
                ],
                samples: data.samples || [],
            });
        } catch (e) {
            setResult({ error: e?.response?.data?.detail || e.message });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="paper-card border border-dashed border-ink/30 p-4 mb-4" data-testid="admin-radio">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink inline-flex items-center gap-2">
                    <RadioIcon className="w-3.5 h-3.5" /> Radio · brand normalisation
                </h3>
                <span className="label-typed text-[10px]">owner only · idempotent</span>
            </div>
            <p className="font-body text-xs text-ink-soft mb-3 max-w-3xl">
                Walks every <code className="text-ink">radio_tracks</code> row. Rewrites
                mis-cased / spaced brand values to canonical snake_case
                (<code className="text-ink">spy_finance</code>,&nbsp;
                <code className="text-ink">garys_shed</code>,&nbsp;
                <code className="text-ink">prisoner</code>,&nbsp;
                <code className="text-ink">crossover</code>). Brandless tracks are
                classified via <code className="text-ink">catalogue_id</code> lookup
                first, then a keyword heuristic on title + prompt. Unclassifiable
                tracks left untouched — original value preserved in{" "}
                <code className="text-ink">brand_pre_normalise</code>.
            </p>
            <div className="flex flex-wrap items-center gap-2">
                <button
                    onClick={() => run(true)}
                    disabled={busy}
                    data-testid="admin-radio-dryrun"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Dry-run
                </button>
                <button
                    onClick={() => {
                        if (window.confirm("Normalise radio_tracks brand values?\n\nIdempotent — re-running is a no-op. Original values preserved in brand_pre_normalise.")) {
                            run(false);
                        }
                    }}
                    disabled={busy}
                    data-testid="admin-radio-run"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {busy ? "Working…" : "Normalise now"}
                </button>
            </div>
            <ResultPanel result={result} />
        </div>
    );
}


function PreWarmPanel() {
    // Iter 31.99 — mint/rotate the bookmarkable pre-warm URL.
    // Bookmark fires a session-start signal to Two without auth.
    const [busy, setBusy] = useState(false);
    const [issued, setIssued] = useState(null);
    const [err, setErr] = useState("");
    const [copied, setCopied] = useState(false);

    const mint = async () => {
        setErr(""); setCopied(false);
        if (!window.confirm(
            "Mint a new pre-warm ping URL?\n\n" +
            "Any previously-bookmarked URL will stop working immediately. " +
            "Show the new URL ONCE — copy it before closing this panel."
        )) return;
        setBusy(true);
        try {
            const { data } = await api.post("/comms/prewarm/key");
            setIssued(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const fireTest = async () => {
        try {
            const { data } = await api.post("/comms/prewarm", {
                note: "Test fire from Admin panel",
            });
            window.alert(`Prewarm filed at ${data.filed_at}\nSubject: ${data.subject}`);
        } catch (e) {
            window.alert(`Test fire failed: ${e?.response?.data?.detail || e.message}`);
        }
    };

    const copy = async () => {
        if (!issued?.url) return;
        await navigator.clipboard.writeText(issued.url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2400);
    };

    return (
        <div className="paper-card border border-dashed border-ink/30 p-4 mb-4" data-testid="admin-prewarm">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink inline-flex items-center gap-2">
                    <ShieldAlert className="w-3.5 h-3.5" /> Session pre-warm
                </h3>
                <span className="label-typed text-[10px]">bookmarkable · no-auth trigger</span>
            </div>
            <p className="font-body text-xs text-ink-soft mb-3 max-w-3xl">
                Mint a permanent URL you can bookmark on phone or browser. Hitting it
                (no login needed — the secret is in the query string) files a session
                pre-warm to Two's outbound. Two sees it on her next <code className="text-ink">/comms/handler-context</code>{" "}
                under the new <code className="text-ink">session_prewarm</code> field and
                walks in already briefed.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
                <button
                    onClick={mint}
                    disabled={busy}
                    data-testid="prewarm-mint"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {busy ? "Minting…" : "Mint / rotate URL"}
                </button>
                <button
                    onClick={fireTest}
                    data-testid="prewarm-test"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red"
                >
                    Fire test prewarm
                </button>
            </div>
            {err && <div className="mt-1 text-stamp-red font-body text-xs" data-testid="prewarm-error">Error: {err}</div>}
            {issued && (
                <div className="mt-2 border-2 border-stamp-red bg-stamp-red/5 p-3" data-testid="prewarm-issued">
                    <div className="label-typed text-[10px] text-stamp-red mb-2">
                        New pre-warm URL · shown ONCE
                    </div>
                    <textarea
                        readOnly
                        value={issued.url}
                        rows={3}
                        onFocus={(e) => e.target.select()}
                        data-testid="prewarm-url"
                        className="w-full px-2 py-1 border border-ink/30 bg-paper-light font-mono text-[10px] text-ink select-all break-all"
                    />
                    <button
                        onClick={copy}
                        data-testid="prewarm-copy"
                        className="mt-2 font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red"
                    >
                        {copied ? "Copied ✓" : "Copy URL"}
                    </button>
                    <div className="font-body text-[11px] text-ink-soft mt-2 leading-relaxed">
                        {issued.note}
                    </div>
                </div>
            )}
        </div>
    );
}


function AgentActivityPanel() {
    // Iter 31.175 (02 Jun 2026) — Per-agent token activity tile.
    // Surfaces scope set, last endpoints touched, 24h sparkline,
    // and the paired TVE refresh credentials minted via iter 31.174.
    // Iter 31.176 (02 Jun 2026) — Drill-down modal + per-row TVE revoke.
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [drillAgent, setDrillAgent] = useState(null);  // {agent_key, agent_name}
    const [revokingId, setRevokingId] = useState(null);
    const [rotatingId, setRotatingId] = useState(null);
    const [rotatedSecret, setRotatedSecret] = useState(null);  // {old_id, new_id, secret, agent_key, scopes, ttl_hours}

    const load = async () => {
        setErr("");
        try {
            const { data } = await api.get("/admin/agent-activity");
            setData(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const fmtTime = (iso) => {
        if (!iso) return "—";
        try {
            const d = new Date(iso);
            return d.toLocaleString("en-GB", {
                day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
            });
        } catch { return iso; }
    };

    const fmtAgo = (iso) => {
        if (!iso) return "—";
        try {
            const ms = Date.now() - new Date(iso).getTime();
            if (ms < 0) return "now";
            const min = Math.floor(ms / 60000);
            if (min < 1) return "just now";
            if (min < 60) return `${min}m ago`;
            const h = Math.floor(min / 60);
            if (h < 24) return `${h}h ago`;
            const d = Math.floor(h / 24);
            return `${d}d ago`;
        } catch { return iso; }
    };

    const revokeTve = async (credId, purpose) => {
        if (!window.confirm(
            `Revoke TVE credential "${purpose}"?\n\n` +
            `Future exchanges of this refresh-secret will be rejected. ` +
            `Agent tokens already in flight remain valid until their TTL.`
        )) return;
        setRevokingId(credId);
        try {
            await api.post(`/auth/service/${credId}/revoke`);
            await load();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setRevokingId(null);
        }
    };

    const rotateTve = async (credId, purpose) => {
        if (!window.confirm(
            `Rotate TVE credential "${purpose}"?\n\n` +
            `Old credential will be REVOKED. A fresh refresh-secret will be ` +
            `issued with the same scopes / ttl / target agent — shown ONCE in ` +
            `this panel. Copy it before closing.`
        )) return;
        setRotatingId(credId);
        try {
            const { data } = await api.post(`/auth/service/${credId}/rotate`);
            setRotatedSecret({
                old_id: credId,
                new_id: data.id,
                secret: data.refresh_secret,
                agent_key: data.target_agent_key,
                scopes: data.scopes,
                ttl_hours: data.ttl_hours,
            });
            await load();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setRotatingId(null);
        }
    };

    return (
        <div className="paper-card border-2 border-ink/30 p-5 mb-6" data-testid="admin-agent-activity">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                <h2 className="font-heading text-sm uppercase tracking-[0.22em] text-ink inline-flex items-center gap-2">
                    <Activity className="w-3.5 h-3.5" /> Agent token activity · last 24h
                </h2>
                <button
                    type="button"
                    onClick={load}
                    className="font-heading uppercase text-[10px] tracking-[0.22em] text-ink-soft hover:text-stamp-red inline-flex items-center gap-1"
                    data-testid="admin-agent-activity-refresh"
                >
                    <RefreshCw className="w-3 h-3" /> Refresh
                </button>
            </div>

            <p className="font-body text-xs text-ink-soft mb-4 max-w-3xl">
                Per-agent telemetry on bearer tokens currently in flight. Rendered from
                the <code className="text-ink">agent_token_activity</code> middleware
                logger (iter 31.175). Shows the scope set the agent is actually using,
                the top endpoints it has touched, and a 24-hour request-volume
                sparkline. Paired TVE refresh credentials (iter 31.174) listed
                underneath. Retention: 7 days. Cleared automatically.
            </p>

            {loading ? (
                <div className="flex items-center gap-2 text-ink-soft text-xs">
                    <Loader2 className="w-3 h-3 animate-spin" /> Reading…
                </div>
            ) : err ? (
                <div className="text-stamp-red font-body text-xs" data-testid="admin-agent-activity-error">
                    Error: {err}
                </div>
            ) : (
                <>
                    {/* Iter 31.180 — Recovery banner. Shown when any agent
                        flipped back below threshold today and we've filed
                        a recovery comms_outbound. Disappears at midnight UTC. */}
                    {(data?.alert_recoveries_today || []).length > 0 && (
                        <div
                            className="mb-4 border-l-4 border-green-700 bg-green-700/10 px-3 py-2 flex flex-wrap items-baseline justify-between gap-2"
                            data-testid="admin-aa-recovery-banner"
                        >
                            <div className="font-heading uppercase tracking-[0.18em] text-[11px] text-green-700">
                                ✓ {data.alert_recoveries_today.length} recovery{data.alert_recoveries_today.length === 1 ? "" : " entries"} today
                            </div>
                            <div className="font-body text-[11px] text-ink-soft">
                                {data.alert_recoveries_today.map((r, i) => (
                                    <span key={r.agent_key} className="text-ink">
                                        {i > 0 ? " · " : ""}{r.agent_key}
                                        {r.channels?.email?.ok ? " (email ✓)" : ""}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Iter 31.178 — Top-level alert banner if any agents are tripping thresholds. */}
                    {(data?.totals?.agents_in_alert ?? 0) > 0 && (
                        <div
                            className="mb-4 border-l-4 border-stamp-red bg-stamp-red/8 px-3 py-2 flex flex-wrap items-baseline justify-between gap-2"
                            data-testid="admin-aa-alert-banner"
                        >
                            <div className="font-heading uppercase tracking-[0.18em] text-[11px] text-stamp-red">
                                ⚠ {data.totals.agents_in_alert} agent{data.totals.agents_in_alert === 1 ? "" : "s"} in alert state
                            </div>
                            <div className="font-body text-[11px] text-ink-soft">
                                {(data?.agents || [])
                                    .filter((a) => a.alert?.triggered)
                                    .map((a) => a.agent_name || a.agent_key)
                                    .join(" · ")}
                            </div>
                            {(data?.alert_dispatches_today || []).length > 0 && (
                                <div
                                    className="font-body text-[10px] text-ink-soft basis-full"
                                    data-testid="admin-aa-dispatches-summary"
                                >
                                    Auto-dispatched to Two today:&nbsp;
                                    {(data.alert_dispatches_today || []).map((d, i) => (
                                        <span key={d.agent_key} className="text-ink">
                                            {i > 0 ? ", " : ""}{d.agent_key}
                                            {d.channels?.email?.ok ? " (email ✓)" : ""}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Totals strip */}
                    <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-4" data-testid="admin-agent-activity-totals">
                        <div>
                            <div className="label-typed text-[10px] mb-0.5">Live tokens</div>
                            <div className="font-heading text-lg text-stamp-red" data-testid="admin-aa-total-live">
                                {data?.totals?.agents_with_live_tokens ?? 0}
                            </div>
                        </div>
                        <div>
                            <div className="label-typed text-[10px] mb-0.5">Active 24h</div>
                            <div className="font-heading text-lg text-ink">
                                {data?.totals?.agents_active_24h ?? 0}
                            </div>
                        </div>
                        <div>
                            <div className="label-typed text-[10px] mb-0.5">Requests 24h</div>
                            <div className="font-heading text-lg text-ink" data-testid="admin-aa-total-requests">
                                {data?.totals?.requests_24h ?? 0}
                            </div>
                        </div>
                        <div>
                            <div className="label-typed text-[10px] mb-0.5">Errors 24h</div>
                            <div className={`font-heading text-lg ${(data?.totals?.errors_24h ?? 0) > 0 ? "text-stamp-red" : "text-ink-soft"}`}>
                                {data?.totals?.errors_24h ?? 0}
                            </div>
                        </div>
                        <div>
                            <div className="label-typed text-[10px] mb-0.5">TVE creds</div>
                            <div className="font-heading text-lg text-ink">
                                {data?.totals?.tve_credentials ?? 0}
                            </div>
                        </div>
                        <div>
                            <div className="label-typed text-[10px] mb-0.5">In alert</div>
                            <div
                                className={`font-heading text-lg ${(data?.totals?.agents_in_alert ?? 0) > 0 ? "text-stamp-red" : "text-ink-soft"}`}
                                data-testid="admin-aa-total-alerts"
                            >
                                {data?.totals?.agents_in_alert ?? 0}
                            </div>
                        </div>
                    </div>

                    {/* Iter 31.178 — Alert thresholds config row. */}
                    {data?.alert_config && (
                        <AlertConfigRow
                            config={data.alert_config}
                            onSaved={load}
                        />
                    )}

                    {/* Per-agent rows */}
                    {(data?.agents || []).length === 0 ? (
                        <div className="font-body text-xs text-ink-soft italic">
                            No agent-token activity in the last 7 days.
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="admin-agent-activity-list">
                            {(data?.agents || []).map((a) => (
                                <AgentActivityRow
                                    key={a.agent_key}
                                    agent={a}
                                    fmtTime={fmtTime}
                                    fmtAgo={fmtAgo}
                                    onDrill={() => setDrillAgent({ agent_key: a.agent_key, agent_name: a.agent_name })}
                                />
                            ))}
                        </div>
                    )}

                    {/* TVE credentials sub-panel */}
                    {(data?.service_credentials || []).length > 0 && (
                        <div className="mt-5 pt-4 border-t border-ink/20" data-testid="admin-aa-tve-list">
                            <div className="label-typed text-[10px] mb-2 text-ink-soft">
                                TVE refresh credentials (iter 31.174)
                            </div>

                            {/* Iter 31.177 — Rotated-secret reveal. Shown ONCE
                                immediately after a successful rotate. Operator
                                copies the new secret then dismisses. */}
                            {rotatedSecret && (
                                <div
                                    className="mb-3 border-2 border-stamp-red bg-stamp-red/5 p-3"
                                    data-testid="admin-aa-rotated-secret"
                                >
                                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                                        <div className="label-typed text-[10px] text-stamp-red">
                                            Rotated credential · shown ONCE
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setRotatedSecret(null)}
                                            data-testid="admin-aa-rotated-dismiss"
                                            className="font-heading uppercase text-[10px] tracking-[0.18em] text-ink-soft hover:text-stamp-red"
                                        >
                                            Dismiss
                                        </button>
                                    </div>
                                    <div className="font-body text-[11px] text-ink-soft mb-2">
                                        Agent <span className="text-ink font-heading">{rotatedSecret.agent_key}</span> ·
                                        scopes <span className="text-ink">{(rotatedSecret.scopes || []).join(", ")}</span> ·
                                        ttl <span className="text-ink">{rotatedSecret.ttl_hours}h</span> ·
                                        old <code className="text-ink font-mono">{rotatedSecret.old_id?.slice(0, 8)}…</code> →
                                        new <code className="text-ink font-mono">{rotatedSecret.new_id?.slice(0, 8)}…</code>
                                    </div>
                                    <textarea
                                        readOnly
                                        value={rotatedSecret.secret}
                                        rows={2}
                                        onFocus={(e) => e.target.select()}
                                        data-testid="admin-aa-rotated-secret-value"
                                        className="w-full px-2 py-1 border border-ink/30 bg-paper-light font-mono text-[10px] text-ink select-all break-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => navigator.clipboard.writeText(rotatedSecret.secret)}
                                        data-testid="admin-aa-rotated-copy"
                                        className="mt-2 font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red"
                                    >
                                        Copy secret
                                    </button>
                                </div>
                            )}

                            <div className="space-y-1.5">
                                {data.service_credentials.map((c) => (
                                    <div
                                        key={c.id}
                                        className="grid grid-cols-1 sm:grid-cols-6 gap-2 text-[11px] font-body items-baseline"
                                        data-testid={`admin-aa-tve-${c.id}`}
                                    >
                                        <div className="font-heading uppercase tracking-[0.12em] text-ink truncate">
                                            {c.target_agent_key}
                                        </div>
                                        <div className="text-ink-soft truncate" title={c.purpose}>
                                            {c.purpose}
                                        </div>
                                        <div className="text-ink-soft">
                                            scopes: <span className="text-ink">{(c.scopes_allowed || []).join(", ")}</span>
                                        </div>
                                        <div className="text-ink-soft">
                                            ttl: <span className="text-ink">{c.ttl_hours}h</span> · exch: <span className="text-ink">{c.exchange_count ?? 0}</span>
                                        </div>
                                        <div className={c.revoked_at ? "text-stamp-red" : "text-ink-soft"}>
                                            {c.revoked_at ? `revoked ${fmtAgo(c.revoked_at)}` : (c.last_used_at ? `used ${fmtAgo(c.last_used_at)}` : "unused")}
                                        </div>
                                        <div className="text-right flex items-center gap-1 justify-end">
                                            {c.revoked_at ? (
                                                <span className="font-heading uppercase text-[9px] tracking-[0.18em] text-ink-soft">—</span>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => rotateTve(c.id, c.purpose)}
                                                        disabled={rotatingId === c.id || revokingId === c.id}
                                                        data-testid={`admin-aa-tve-rotate-${c.id}`}
                                                        className="font-heading uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red disabled:opacity-40 disabled:cursor-not-allowed"
                                                    >
                                                        {rotatingId === c.id ? "…" : "Rotate"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => revokeTve(c.id, c.purpose)}
                                                        disabled={revokingId === c.id || rotatingId === c.id}
                                                        data-testid={`admin-aa-tve-revoke-${c.id}`}
                                                        className="font-heading uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-40 disabled:cursor-not-allowed"
                                                    >
                                                        {revokingId === c.id ? "…" : "Revoke"}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Iter 31.176 — Drill-down modal */}
            <AgentActivityDrillModal
                target={drillAgent}
                onClose={() => setDrillAgent(null)}
            />
        </div>
    );
}

function AgentActivityDrillModal({ target, onClose }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!target?.agent_key) return;
        let cancelled = false;
        setLoading(true);
        setErr("");
        setRows([]);
        (async () => {
            try {
                const { data } = await api.get(
                    `/admin/agent-activity/${target.agent_key}/recent?limit=50`
                );
                if (!cancelled) setRows(data.rows || []);
            } catch (e) {
                if (!cancelled) setErr(e?.response?.data?.detail || e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [target?.agent_key]);

    const open = !!target;

    const fmtTs = (iso) => {
        if (!iso) return "—";
        try {
            const d = new Date(iso);
            return d.toLocaleString("en-GB", {
                day: "2-digit", month: "short",
                hour: "2-digit", minute: "2-digit", second: "2-digit",
            });
        } catch { return iso; }
    };

    const exportCsv = async () => {
        if (!target?.agent_key) return;
        try {
            const { data, headers } = await api.get(
                `/admin/agent-activity/${target.agent_key}/export.csv?limit=500`,
                { responseType: "blob" }
            );
            // Pull filename from Content-Disposition if present.
            const cd = headers["content-disposition"] || "";
            const m = /filename="?([^"]+)"?/.exec(cd);
            const filename = m ? m[1] : `agent-activity-${target.agent_key}.csv`;
            const blob = new Blob([data], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent
                // Iter 31.183 (03 Feb 2026) — Bypass shadcn Dialog's
                // default bg-background. On the spy-finance skin that
                // token resolves to deep midnight (#0a1426) and overrode
                // our paper-card surface in the production build, so One
                // saw "a screen over my screen but i cant see anything
                // on it". CreditWallSheet hit the same trap and resolved
                // it the same way: transparent DialogContent + explicit
                // inner panel that uses the RGB-channel mirrors so it
                // renders correctly in BOTH skins.
                className="max-w-3xl p-0 border-0 bg-transparent shadow-none"
                data-testid="admin-aa-drill-modal"
            >
                <div className="bg-[rgb(var(--c-paper))] text-[rgb(var(--c-ink))] border-2 border-stamp-red shadow-2xl">
                <div className="px-5 py-4 border-b border-ink/20 flex items-baseline justify-between gap-3">
                    <div>
                        <DialogTitle className="font-heading uppercase tracking-[0.18em] text-sm text-stamp-red">
                            Drill · {target?.agent_name || target?.agent_key}
                        </DialogTitle>
                        <DialogDescription className="font-body text-[11px] text-ink-soft mt-1">
                            Most recent 50 agent-token requests · timestamp, method, path, status, IP.
                            Retention 7d (iter 31.175). Evidence surface for Sixty-Six.
                        </DialogDescription>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={exportCsv}
                            data-testid="admin-aa-drill-export-csv"
                            className="font-heading uppercase text-[10px] tracking-[0.18em] px-2 py-1 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red"
                            disabled={rows.length === 0}
                        >
                            Export CSV
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="admin-aa-drill-close"
                            className="text-ink-soft hover:text-stamp-red"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center gap-2 text-ink-soft text-xs">
                            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                        </div>
                    ) : err ? (
                        <div className="text-stamp-red font-body text-xs" data-testid="admin-aa-drill-error">
                            Error: {err}
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="font-body text-xs text-ink-soft italic">
                            No requests on record for this agent in the last 7 days.
                        </div>
                    ) : (
                        <table className="w-full text-[11px] font-mono" data-testid="admin-aa-drill-table">
                            <thead>
                                <tr className="text-left text-ink-soft border-b border-ink/20">
                                    <th className="py-1 pr-2">when</th>
                                    <th className="py-1 pr-2">method</th>
                                    <th className="py-1 pr-2">path</th>
                                    <th className="py-1 pr-2">status</th>
                                    <th className="py-1 pr-2">ip</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr
                                        key={r.id || i}
                                        className="border-b border-ink/10 last:border-b-0"
                                        data-testid={`admin-aa-drill-row-${i}`}
                                    >
                                        <td className="py-1 pr-2 text-ink-soft whitespace-nowrap">{fmtTs(r.ts)}</td>
                                        <td className="py-1 pr-2 text-ink">{r.method}</td>
                                        <td className="py-1 pr-2 text-ink truncate" title={r.path}>{r.path}</td>
                                        <td className={`py-1 pr-2 ${r.status_code >= 400 ? "text-stamp-red" : "text-ink"}`}>
                                            {r.status_code}
                                        </td>
                                        <td className="py-1 pr-2 text-ink-soft">{r.ip || "?"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function AlertHistoryPanel() {
    // Iter 31.187 (03 Feb 2026) — UI for the existing
    // /admin/agent-activity/alert-dispatches and /digests endpoints
    // (iters 31.179 + 31.181). Data has been on the wire — this is
    // the readable surface so Sixty-Six's audit cycle has somewhere
    // human-friendly to point One at.
    const [dispatches, setDispatches] = useState([]);
    const [digests, setDigests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [tab, setTab] = useState("dispatches");  // "dispatches" | "digests"

    const load = async () => {
        setErr("");
        setLoading(true);
        try {
            const [d, g] = await Promise.all([
                api.get("/admin/agent-activity/alert-dispatches?limit=50"),
                api.get("/admin/agent-activity/digests?limit=30"),
            ]);
            setDispatches(d.data.dispatches || []);
            setDigests(g.data.digests || []);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const fmtTs = (iso) => {
        if (!iso) return "—";
        try {
            const d = new Date(iso);
            return d.toLocaleString("en-GB", {
                day: "2-digit", month: "short",
                hour: "2-digit", minute: "2-digit",
            });
        } catch { return iso; }
    };

    return (
        <div className="paper-card border-2 border-ink/30 p-5 mb-6" data-testid="admin-alert-history">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                <h2 className="font-heading text-sm uppercase tracking-[0.22em] text-ink">
                    Alert history
                </h2>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setTab("dispatches")}
                        data-testid="admin-alert-history-tab-dispatches"
                        className={`font-heading uppercase text-[10px] tracking-[0.22em] px-2 py-1 border ${
                            tab === "dispatches"
                                ? "border-stamp-red text-stamp-red"
                                : "border-ink/30 text-ink-soft hover:text-stamp-red"
                        }`}
                    >
                        Dispatches ({dispatches.length})
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab("digests")}
                        data-testid="admin-alert-history-tab-digests"
                        className={`font-heading uppercase text-[10px] tracking-[0.22em] px-2 py-1 border ${
                            tab === "digests"
                                ? "border-stamp-red text-stamp-red"
                                : "border-ink/30 text-ink-soft hover:text-stamp-red"
                        }`}
                    >
                        Digests ({digests.length})
                    </button>
                    <button
                        type="button"
                        onClick={load}
                        className="font-heading uppercase text-[10px] tracking-[0.22em] text-ink-soft hover:text-stamp-red inline-flex items-center gap-1"
                        data-testid="admin-alert-history-refresh"
                    >
                        <RefreshCw className="w-3 h-3" /> Refresh
                    </button>
                </div>
            </div>
            <p className="font-body text-xs text-ink-soft mb-3 max-w-3xl">
                Dispatches: per-agent alert + recovery rows (iters 31.179–31.180).
                Digests: 09:00 UTC daily rollups (iter 31.181). Both already file
                to Two via comms_outbound + (when wired) Resend email.
            </p>

            {loading ? (
                <div className="flex items-center gap-2 text-ink-soft text-xs">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                </div>
            ) : err ? (
                <div className="text-stamp-red font-body text-xs" data-testid="admin-alert-history-error">
                    Error: {err}
                </div>
            ) : tab === "dispatches" ? (
                dispatches.length === 0 ? (
                    <div className="font-body text-xs text-ink-soft italic">
                        No alert dispatches on record.
                    </div>
                ) : (
                    <div className="space-y-2" data-testid="admin-alert-history-dispatch-list">
                        {dispatches.map((d) => (
                            <div
                                key={d.id}
                                className={`border-l-2 pl-3 py-1.5 ${d.recovered_at ? "border-green-700" : "border-stamp-red"}`}
                                data-testid={`admin-alert-history-dispatch-${d.id}`}
                            >
                                <div className="flex flex-wrap items-baseline justify-between gap-2">
                                    <div className="font-heading uppercase tracking-[0.18em] text-xs text-ink">
                                        {d.agent_name || d.agent_key}
                                        <span className="text-ink-soft text-[10px] normal-case ml-2">
                                            {fmtTs(d.filed_at)}
                                        </span>
                                    </div>
                                    {d.recovered_at ? (
                                        <span className="font-heading uppercase text-[9px] tracking-[0.18em] text-paper bg-green-700 px-1.5 py-0.5">
                                            ✓ Recovered {fmtTs(d.recovered_at)}
                                        </span>
                                    ) : (
                                        <span className="font-heading uppercase text-[9px] tracking-[0.18em] text-paper bg-stamp-red px-1.5 py-0.5">
                                            ⚠ Unresolved
                                        </span>
                                    )}
                                </div>
                                <div className="font-body text-[11px] text-ink-soft mt-0.5">
                                    {d.reason} · {d.err_24h}/{d.total_24h} reqs (
                                    {((d.error_rate || 0) * 100).toFixed(1)}%)
                                </div>
                                <div className="font-mono text-[10px] text-ink-soft mt-0.5">
                                    {d.channels?.comms_outbound?.ok ? "comms ✓" : "comms ✗"}
                                    {" · "}
                                    {d.channels?.email?.ok ? "email ✓" : "email ✗"}
                                </div>
                            </div>
                        ))}
                    </div>
                )
            ) : (
                digests.length === 0 ? (
                    <div className="font-body text-xs text-ink-soft italic">
                        No daily digests on record. (Scheduler fires at 09:00 UTC; backfill via POST /digest/run.)
                    </div>
                ) : (
                    <div className="space-y-2" data-testid="admin-alert-history-digest-list">
                        {digests.map((g) => (
                            <div
                                key={g.id}
                                className={`border-l-2 pl-3 py-1.5 ${g.n_unresolved > 0 ? "border-stamp-red" : "border-ink/30"}`}
                                data-testid={`admin-alert-history-digest-${g.id}`}
                            >
                                <div className="flex flex-wrap items-baseline justify-between gap-2">
                                    <div className="font-heading uppercase tracking-[0.18em] text-xs text-ink">
                                        {g.date}
                                    </div>
                                    <div className="font-body text-[11px]">
                                        <span className="text-stamp-red">{g.n_alerts}</span> alerts · 
                                        <span className="text-green-700 ml-1">{g.n_recoveries}</span> recoveries
                                        {(g.n_unresolved || 0) > 0 && (
                                            <span className="text-stamp-red ml-2">· {g.n_unresolved} unresolved</span>
                                        )}
                                    </div>
                                </div>
                                <div className="font-mono text-[10px] text-ink-soft mt-0.5">
                                    filed {fmtTs(g.filed_at)}
                                    {" · "}
                                    {g.channels?.comms_outbound?.ok ? "comms ✓" : "comms ✗"}
                                    {" · "}
                                    {g.channels?.email?.ok ? "email ✓" : "email ✗"}
                                </div>
                                {(g.agents || []).length > 0 && (
                                    <div className="font-body text-[11px] text-ink-soft mt-1">
                                        {g.agents.map((a) => a.agent_name).join(" · ")}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )
            )}
        </div>
    );
}


function AlertConfigRow({ config, onSaved }) {
    // Iter 31.178 — collapsible threshold editor.
    const [open, setOpen] = useState(false);
    const [errRate, setErrRate] = useState(config.error_rate_threshold);
    const [minN, setMinN] = useState(config.min_sample_size);
    const [floor, setFloor] = useState(config.absolute_error_floor);
    const [busy, setBusy] = useState(false);
    const [saveErr, setSaveErr] = useState("");

    const save = async () => {
        setBusy(true);
        setSaveErr("");
        try {
            await api.put("/admin/agent-activity/alerts/config", {
                error_rate_threshold: Number(errRate),
                min_sample_size: Number(minN),
                absolute_error_floor: Number(floor),
            });
            setOpen(false);
            onSaved && (await onSaved());
        } catch (e) {
            setSaveErr(e?.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mb-4 border border-dashed border-ink/30 px-3 py-2" data-testid="admin-aa-alert-config">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="font-body text-[11px] text-ink-soft">
                    Alert thresholds:&nbsp;
                    <span className="text-ink font-heading">{Math.round(config.error_rate_threshold * 100)}%</span> err
                    over <span className="text-ink font-heading">{config.min_sample_size}</span> reqs,
                    or <span className="text-ink font-heading">{config.absolute_error_floor}</span>+ errors regardless.
                </div>
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    data-testid="admin-aa-alert-config-toggle"
                    className="font-heading uppercase text-[10px] tracking-[0.18em] text-ink-soft hover:text-stamp-red"
                >
                    {open ? "Cancel" : "Tune →"}
                </button>
            </div>
            {open && (
                <div className="mt-2 grid grid-cols-3 gap-2 items-end">
                    <label className="block">
                        <div className="label-typed text-[10px] mb-0.5">Error rate (0–1)</div>
                        <input
                            type="number"
                            step="0.05"
                            min="0"
                            max="1"
                            value={errRate}
                            onChange={(e) => setErrRate(e.target.value)}
                            data-testid="admin-aa-alert-config-error-rate"
                            className="w-full px-2 py-1 border border-ink/30 bg-paper-light font-mono text-[11px] text-ink"
                        />
                    </label>
                    <label className="block">
                        <div className="label-typed text-[10px] mb-0.5">Min sample (24h)</div>
                        <input
                            type="number"
                            min="1"
                            max="10000"
                            value={minN}
                            onChange={(e) => setMinN(e.target.value)}
                            data-testid="admin-aa-alert-config-min-sample"
                            className="w-full px-2 py-1 border border-ink/30 bg-paper-light font-mono text-[11px] text-ink"
                        />
                    </label>
                    <label className="block">
                        <div className="label-typed text-[10px] mb-0.5">Absolute err floor</div>
                        <input
                            type="number"
                            min="1"
                            max="100000"
                            value={floor}
                            onChange={(e) => setFloor(e.target.value)}
                            data-testid="admin-aa-alert-config-floor"
                            className="w-full px-2 py-1 border border-ink/30 bg-paper-light font-mono text-[11px] text-ink"
                        />
                    </label>
                    <div className="col-span-3 flex items-center justify-end gap-2 mt-1">
                        {saveErr && <span className="text-stamp-red font-body text-[11px]">Error: {saveErr}</span>}
                        <button
                            type="button"
                            onClick={save}
                            disabled={busy}
                            data-testid="admin-aa-alert-config-save"
                            className="font-heading uppercase text-[10px] tracking-[0.18em] px-3 py-1 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {busy ? "Saving…" : "Save"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Sparkline({ values, testid }) {
    // Tiny inline-SVG sparkline. 24 buckets, oldest left, newest right.
    if (!Array.isArray(values) || values.length === 0) {
        return <div className="font-body text-[10px] text-ink-soft italic">no data</div>;
    }    const w = 120;
    const h = 24;
    const max = Math.max(1, ...values);
    const barW = w / values.length;
    return (
        <svg width={w} height={h} className="block" data-testid={testid}>
            {values.map((v, i) => {
                const bh = Math.max(v > 0 ? 2 : 0, (v / max) * (h - 2));
                return (
                    <rect
                        key={i}
                        x={i * barW + 0.5}
                        y={h - bh}
                        width={Math.max(1, barW - 1)}
                        height={bh}
                        fill={v > 0 ? "var(--c-stamp-red, #b22222)" : "transparent"}
                        opacity={v > 0 ? 0.85 : 0}
                    />
                );
            })}
        </svg>
    );
}

function AgentActivityRow({ agent, fmtTime, fmtAgo, onDrill }) {
    const a = agent;
    const alerting = !!a.alert?.triggered;
    return (
        <div
            className={`border-l-2 pl-3 py-2 ${alerting ? "border-stamp-red bg-stamp-red/5" : (a.has_live_token ? "border-stamp-red" : "border-ink/30")}`}
            data-testid={`admin-aa-row-${a.agent_key}`}
        >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="font-heading uppercase tracking-[0.18em] text-sm text-ink flex items-baseline gap-2">
                    <span>
                        {a.agent_number != null ? `${a.agent_number} · ` : ""}{a.agent_name || a.agent_key}
                        {a.agent_alias ? <span className="text-ink-soft text-[11px] normal-case ml-1">({a.agent_alias})</span> : null}
                    </span>
                    {a.alert?.triggered && (
                        <span
                            className="font-heading uppercase tracking-[0.18em] text-[10px] text-paper bg-stamp-red px-1.5 py-0.5"
                            data-testid={`admin-aa-row-${a.agent_key}-alert-badge`}
                            title={a.alert.reason}
                        >
                            ⚠ Alert
                        </span>
                    )}
                    {a.alert?.triggered && a.alert?.last_dispatch_at && (
                        <span
                            className="font-body text-[10px] text-ink-soft"
                            data-testid={`admin-aa-row-${a.agent_key}-dispatched`}
                            title={`Filed to Two ${a.alert.last_dispatch_at}` + (a.alert?.dispatch_channels?.email?.ok ? " · email sent" : "")}
                        >
                            ✓ dispatched
                            {a.alert?.dispatch_channels?.email?.ok ? " · email" : ""}
                        </span>
                    )}
                    {!a.alert?.triggered && a.alert?.recovered_at && (
                        <span
                            className="font-heading uppercase tracking-[0.18em] text-[10px] text-paper bg-green-700 px-1.5 py-0.5"
                            data-testid={`admin-aa-row-${a.agent_key}-recovered-badge`}
                            title={`Recovery filed to Two ${a.alert.recovered_at}` + (a.alert?.recovery_channels?.email?.ok ? " · email sent" : "")}
                        >
                            ✓ Recovered
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 font-body text-[11px]">
                    {a.has_live_token ? (
                        <span className="font-heading uppercase text-[10px] tracking-[0.18em] text-stamp-red border border-stamp-red px-1.5 py-0.5">live</span>
                    ) : (
                        <span className="font-heading uppercase text-[10px] tracking-[0.18em] text-ink-soft border border-ink/30 px-1.5 py-0.5">offline</span>
                    )}
                    <span className="text-ink-soft">v{a.token_version}</span>
                    <button
                        type="button"
                        onClick={onDrill}
                        data-testid={`admin-aa-row-${a.agent_key}-drill`}
                        className="font-heading uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red"
                    >
                        Drill →
                    </button>
                </div>
            </div>

            {alerting && a.alert?.reason && (
                <div
                    className="mt-1 font-body text-[11px] text-stamp-red"
                    data-testid={`admin-aa-row-${a.agent_key}-alert-reason`}
                >
                    {a.alert.reason}
                </div>
            )}

            <div className="grid sm:grid-cols-3 gap-3 mt-1.5">
                <div>
                    <div className="label-typed text-[10px] text-ink-soft">Scopes</div>
                    <div className="font-body text-[11px] text-ink leading-tight">
                        {(a.active_scopes || []).length === 0 ? (
                            <span className="text-ink-soft italic">—</span>
                        ) : (
                            a.active_scopes.map((s) => (
                                <span key={s} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 bg-ink/5 border border-ink/15 font-mono text-[10px]">
                                    {s}
                                </span>
                            ))
                        )}
                    </div>
                    <div className="label-typed text-[10px] mt-2 text-ink-soft">Last used</div>
                    <div className="font-body text-[11px] text-ink" data-testid={`admin-aa-row-${a.agent_key}-last-used`}>
                        {a.last_used_at ? fmtAgo(a.last_used_at) : <span className="text-ink-soft italic">never</span>}
                    </div>
                </div>

                <div>
                    <div className="label-typed text-[10px] text-ink-soft">Top endpoints · 24h</div>
                    <div className="font-mono text-[10px] text-ink leading-tight" data-testid={`admin-aa-row-${a.agent_key}-endpoints`}>
                        {(a.top_endpoints || []).length === 0 ? (
                            <span className="text-ink-soft italic">no requests</span>
                        ) : (
                            a.top_endpoints.map((e) => (
                                <div key={e.path} className="truncate" title={e.path}>
                                    <span className="text-stamp-red mr-1">{e.count}x</span>{e.path}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div>
                    <div className="label-typed text-[10px] text-ink-soft">Sparkline · 24h</div>
                    <Sparkline values={a.hourly_sparkline} testid={`admin-aa-row-${a.agent_key}-sparkline`} />
                    <div className="font-body text-[10px] text-ink-soft mt-1">
                        24h <span className="text-ink font-heading">{a.total_24h}</span> · 7d <span className="text-ink font-heading">{a.total_7d}</span>
                        {a.err_24h > 0 ? <span className="text-stamp-red ml-2">err {a.err_24h}</span> : null}
                    </div>
                    {a.token_issued_at && (
                        <div className="font-body text-[10px] text-ink-soft mt-1" title={`Issued by ${a.token_issued_by || "?"}`}>
                            issued {fmtTime(a.token_issued_at)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


function AgentTokenPanel() {
    // Iter 31.95 — Issue a permanent agent service token from the UI
    // instead of curl. Defaults to Two (key=claude) with comms+credits
    // scope. Token shown ONCE; copy button + warning.
    const [key, setKey] = useState("claude");
    const [scopeComms, setScopeComms] = useState(true);
    const [scopeCredits, setScopeCredits] = useState(true);
    const [scopeSpyfinance, setScopeSpyfinance] = useState(false);
    const [scopeLedger, setScopeLedger] = useState(false);
    const [scopeContext, setScopeContext] = useState(false);
    const [scopeRosterWrite, setScopeRosterWrite] = useState(false);
    const [scopeContextWrite, setScopeContextWrite] = useState(false);
    const [scopeAgentBriefingWrite, setScopeAgentBriefingWrite] = useState(false);
    const [busy, setBusy] = useState(false);
    const [issued, setIssued] = useState(null);
    const [err, setErr] = useState("");
    const [copied, setCopied] = useState(false);
    // Iter 31.107 — optional Drive write-back. When set, token is
    // auto-written to that Drive file in the same call (e.g. Two's
    // `two_token.txt` which she polls at session start).
    const [driveFileId, setDriveFileId] = useState("");

    const submit = async () => {
        setErr(""); setIssued(null); setCopied(false);
        const scopes = [];
        if (scopeComms) scopes.push("comms");
        if (scopeCredits) scopes.push("credits");
        if (scopeSpyfinance) scopes.push("spyfinance");
        if (scopeLedger) scopes.push("ledger");
        if (scopeContext) scopes.push("context");
        if (scopeRosterWrite) scopes.push("roster_write");
        if (scopeContextWrite) scopes.push("context_write");
        if (scopeAgentBriefingWrite) scopes.push("agent_briefing_write");
        if (scopes.length === 0) {
            setErr("Pick at least one scope.");
            return;
        }
        const driveSuffix = driveFileId.trim() ? ` AND overwrite Drive file ${driveFileId.trim().slice(0, 20)}…` : "";
        if (!window.confirm(
            `Issue a permanent token for '${key}' with scopes [${scopes.join(", ")}]${driveSuffix}?\n\n` +
            `Any previously-issued token for this agent will be REVOKED immediately. ` +
            `The new token is shown ONCE — copy it before closing this panel.`
        )) return;
        setBusy(true);
        try {
            // If drive_file_id is supplied, use the combined rotate+write
            // endpoint so the token rotation and Drive write happen atomically.
            const endpoint = driveFileId.trim()
                ? `/agents/${key}/issue-token-to-drive`
                : `/agents/${key}/issue-token`;
            const body = driveFileId.trim()
                ? { scopes, drive_file_id: driveFileId.trim() }
                : { scopes };
            const { data } = await api.post(endpoint, body);
            setIssued(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        if (!issued?.token) return;
        await navigator.clipboard.writeText(issued.token);
        setCopied(true);
        setTimeout(() => setCopied(false), 2400);
    };

    return (
        <div className="paper-card border border-dashed border-ink/30 p-4 mb-4" data-testid="admin-agent-token">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink inline-flex items-center gap-2">
                    <ShieldAlert className="w-3.5 h-3.5" /> Agent service tokens
                </h3>
                <span className="label-typed text-[10px]">owner only · rotates on issue</span>
            </div>
            <p className="font-body text-xs text-ink-soft mb-3 max-w-3xl">
                Mint a permanent bearer token for an agent. Token carries no expiry —
                stays valid until you rotate by re-issuing or revoke via
                <code className="text-ink"> DELETE /agents/{"{key}"}/token</code>.
                Two's key is <code className="text-ink">claude</code>. Use{" "}
                <code className="text-ink">comms</code> for read/file briefings,
                <code className="text-ink"> credits</code> for refund authority,
                <code className="text-ink"> spyfinance</code> for ledger writes (Two's Wallet sync),
                <code className="text-ink"> ledger</code> for life-entries + briefs + agent registry reads,
                <code className="text-ink"> context</code> for addresses + weather + calendar + contacts (read),
                <code className="text-ink"> roster_write</code> for agent creation + amendment (merchant-class),
                <code className="text-ink"> context_write</code> for addresses + weather + bulletins + devices (non-destructive),
                <code className="text-ink"> agent_briefing_write</code> for foundry + portraits + drive-summary refresh.
            </p>
            <div className="mb-2">
                <input
                    type="text"
                    value={key}
                    onChange={(e) => setKey(e.target.value.toLowerCase().trim())}
                    placeholder="agent key (e.g. claude)"
                    data-testid="agent-token-key"
                    className="w-full max-w-sm px-3 py-1.5 border border-ink/30 bg-paper-light font-body text-sm focus:outline-none focus:border-stamp-red"
                />
                <div
                    className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-1.5 font-body text-sm text-ink"
                    data-testid="agent-token-scopes-row"
                >
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={scopeComms}
                            onChange={(e) => setScopeComms(e.target.checked)}
                            data-testid="agent-token-scope-comms"
                        /> comms
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={scopeCredits}
                            onChange={(e) => setScopeCredits(e.target.checked)}
                            data-testid="agent-token-scope-credits"
                        /> credits
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={scopeSpyfinance}
                            onChange={(e) => setScopeSpyfinance(e.target.checked)}
                            data-testid="agent-token-scope-spyfinance"
                        /> spyfinance
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={scopeLedger}
                            onChange={(e) => setScopeLedger(e.target.checked)}
                            data-testid="agent-token-scope-ledger"
                        /> ledger
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={scopeContext}
                            onChange={(e) => setScopeContext(e.target.checked)}
                            data-testid="agent-token-scope-context"
                        /> context
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={scopeRosterWrite}
                            onChange={(e) => setScopeRosterWrite(e.target.checked)}
                            data-testid="agent-token-scope-roster-write"
                        /> roster_write
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={scopeContextWrite}
                            onChange={(e) => setScopeContextWrite(e.target.checked)}
                            data-testid="agent-token-scope-context-write"
                        /> context_write
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={scopeAgentBriefingWrite}
                            onChange={(e) => setScopeAgentBriefingWrite(e.target.checked)}
                            data-testid="agent-token-scope-agent-briefing-write"
                        /> agent_briefing_write
                    </label>
                </div>
            </div>
            <div className="mb-3 border-l-2 border-ink/15 pl-3 ml-1">
                <label className="font-body text-xs text-ink-soft block mb-1">
                    Optional · Drive file ID to overwrite with the new token
                </label>
                <input
                    type="text"
                    value={driveFileId}
                    onChange={(e) => setDriveFileId(e.target.value)}
                    placeholder="e.g. 1UBdhdzd8j4xCjSxrwQH_LpUbIeLp37bz (Two's two_token.txt)"
                    data-testid="agent-token-drive-file-id"
                    className="w-full px-3 py-1.5 border border-ink/30 bg-paper-light font-mono text-[10px] text-ink focus:outline-none focus:border-stamp-red"
                />
                <div className="font-body text-[10px] text-ink-soft mt-1">
                    If set, the new JWT is also written into this Drive file so
                    agents that pull session tokens from Drive (Two via
                    <code className="text-ink"> two_token.txt</code>) don't go stale on rotation.
                    Drive must be connected for the operator firing this.
                </div>
            </div>
            <button
                onClick={submit}
                disabled={busy}
                data-testid="agent-token-issue"
                className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-40 disabled:cursor-not-allowed"
            >
                {busy ? "Minting…" : "Issue permanent token"}
            </button>
            {err && <div className="mt-3 text-stamp-red font-body text-xs" data-testid="agent-token-error">Error: {err}</div>}
            {issued && (
                <div className="mt-4 border-2 border-stamp-red bg-stamp-red/5 p-3" data-testid="agent-token-issued">
                    <div className="label-typed text-[10px] text-stamp-red mb-2">
                        Token for {issued.agent_name} · v{issued.token_version} · {issued.scopes.join(" · ")}
                    </div>
                    <div className="font-body text-[11px] text-ink-soft mb-2 leading-relaxed">
                        <strong className="text-stamp-red">Shown ONCE.</strong> Copy it now, hand it to the agent through a secure channel. Closing this panel without copying means re-issuing (which rotates again).
                    </div>
                    <textarea
                        readOnly
                        value={issued.token}
                        rows={4}
                        onFocus={(e) => e.target.select()}
                        data-testid="agent-token-value"
                        className="w-full px-2 py-1 border border-ink/30 bg-paper-light font-mono text-[10px] text-ink select-all"
                    />
                    <button
                        onClick={copy}
                        data-testid="agent-token-copy"
                        className="mt-2 font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red"
                    >
                        {copied ? "Copied ✓" : "Copy token"}
                    </button>
                    {issued.drive_write && (
                        <div
                            className={`mt-3 px-2 py-2 border ${issued.drive_write.ok ? "border-stamp-green text-stamp-green" : "border-stamp-red text-stamp-red"} font-body text-[11px]`}
                            data-testid="agent-token-drive-write"
                        >
                            {issued.drive_write.ok ? (
                                <>
                                    Drive write OK · {issued.drive_write.bytes_written}B → <code>{issued.drive_write.name || issued.drive_write.drive_file_id}</code> at {issued.drive_write.modified_time}
                                </>
                            ) : (
                                <>Drive write failed: {issued.drive_write.error}</>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


function GrantToEmailPanel() {
    // Iter 31.93 — Six's "refund the 100 credits I burned on broken
    // Brief retries to emes.m.andrews@gmail.com". Run from 6/9 (internal
    // role), target any email registered in db.users.
    const [email, setEmail] = useState("");
    const [credits, setCredits] = useState(100);
    const [note, setNote] = useState("Goodwill — broken Brief burn refund (iter 31.93)");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async () => {
        if (!email || credits <= 0) {
            setResult({ error: "Email and positive credit count required." });
            return;
        }
        if (!window.confirm(`Grant ${credits} credits to ${email}?`)) return;
        setBusy(true); setResult(null);
        try {
            const { data } = await api.post("/credits/admin-grant", {
                credits, note, target_email: email,
            });
            setResult({
                testid: "admin-grant-result",
                summary: `Granted ${credits} credits to ${data.target_email || "self"}.`,
                lines: [
                    `Target user id: ${data.target_user_id}`,
                    `New balance: ${data.balance ?? data?.row?.balance_after ?? "—"}`,
                ],
            });
        } catch (e) {
            setResult({ error: e?.response?.data?.detail || e.message });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="paper-card border border-dashed border-ink/30 p-4 mb-4" data-testid="admin-grant-to">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink inline-flex items-center gap-2">
                    <Gift className="w-3.5 h-3.5" /> Grant credits to another user
                </h3>
                <span className="label-typed text-[10px]">owner only · ledger-mirrored</span>
            </div>
            <p className="font-body text-xs text-ink-soft mb-3 max-w-3xl">
                Internal-only. Use to refund a user whose credits were consumed
                by a failed action that didn't auto-refund (the iter 31.93 Brief-burn
                incident is the canonical example). Files a <code className="text-ink">grant_promo</code>{" "}
                row in the immutable ledger with the granter's email + the target's email.
            </p>
            <div className="grid md:grid-cols-3 gap-2 mb-2">
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="target@example.com"
                    data-testid="admin-grant-email"
                    className="px-3 py-1.5 border border-ink/30 bg-paper-light font-body text-sm focus:outline-none focus:border-stamp-red"
                />
                <input
                    type="number"
                    value={credits}
                    onChange={(e) => setCredits(parseInt(e.target.value, 10) || 0)}
                    min="1"
                    data-testid="admin-grant-credits"
                    className="px-3 py-1.5 border border-ink/30 bg-paper-light font-body text-sm focus:outline-none focus:border-stamp-red"
                />
                <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Note (audit trail)"
                    data-testid="admin-grant-note"
                    className="px-3 py-1.5 border border-ink/30 bg-paper-light font-body text-sm focus:outline-none focus:border-stamp-red"
                />
            </div>
            <button
                onClick={submit}
                disabled={busy}
                data-testid="admin-grant-submit"
                className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-40 disabled:cursor-not-allowed"
            >
                {busy ? "Granting…" : "Grant credits"}
            </button>
            <ResultPanel result={result} />
        </div>
    );
}


function ContactsAuditPanel() {
    // Iter 31.110 (29 May 2026) — quiet timeline of Agent 3's autonomous
    // contacts-audit sweeps + a manual "Run sweep now" button. Two's
    // pattern: surface autonomous work where Six can audit it without
    // dredging the ledger.
    const [status, setStatus] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [last, setLast] = useState(null);

    const load = async () => {
        setErr("");
        try {
            const [s, h] = await Promise.all([
                api.get("/contacts/audit/status"),
                api.get("/contacts/audit/history?limit=20"),
            ]);
            setStatus(s.data);
            setHistory(h.data.runs || []);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const runSweep = async () => {
        if (!window.confirm("Fire a contacts-audit sweep now?\n\nIdempotent — re-running with a clean ledger files zero corrections. Originals untouched (Canon Rule 1).")) return;
        setBusy(true); setErr("");
        try {
            const { data } = await api.post("/contacts/audit/apply");
            setLast(data);
            await load();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const formatTime = (iso) => {
        if (!iso) return "—";
        try {
            const d = new Date(iso);
            return d.toLocaleString("en-GB", {
                day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
            });
        } catch { return iso; }
    };

    return (
        <div className="paper-card border border-dashed border-ink/30 p-4 mb-4" data-testid="admin-contacts-audit">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink">
                    Contacts · Agent 3 autonomous audit
                </h3>
                <span className="label-typed text-[10px]">D1 · D2 · D3 · D4 · D5 stub</span>
            </div>
            <p className="font-body text-xs text-ink-soft mb-3 max-w-3xl">
                Agent 3 sweeps the rolodex on a quiet cadence (default 6h) and files
                <code className="text-ink"> linkedin_contact_correction</code> rows when
                a contact has demonstrable gaps — name splits, URL canonicalisation,
                company extraction from position strings. Originals never altered;
                aggregator merges corrections at read time.
            </p>

            {loading ? (
                <div className="font-body text-xs text-ink-soft">Loading…</div>
            ) : err ? (
                <div className="text-stamp-red font-body text-xs" data-testid="admin-contacts-audit-error">
                    Error: {err}
                </div>
            ) : (
                <>
                    {/* Status strip */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="admin-contacts-audit-status">
                        <div>
                            <div className="label-typed text-[10px] mb-0.5">Loop</div>
                            <div className="font-heading text-sm">
                                {status?.autonomous_enabled && status?.loop_running ? (
                                    <span className="text-stamp-red">RUNNING</span>
                                ) : (
                                    <span className="text-ink-soft">paused</span>
                                )}
                            </div>
                        </div>
                        <div>
                            <div className="label-typed text-[10px] mb-0.5">Cadence</div>
                            <div className="font-heading text-sm text-ink">{status?.interval_hours}h</div>
                        </div>
                        <div>
                            <div className="label-typed text-[10px] mb-0.5">Corrections filed</div>
                            <div className="font-heading text-sm text-ink" data-testid="admin-contacts-audit-total">
                                {status?.corrections_filed_total ?? 0}
                            </div>
                        </div>
                        <div>
                            <div className="label-typed text-[10px] mb-0.5">By detector</div>
                            <div className="font-body text-[11px] text-ink leading-tight">
                                {Object.keys(status?.by_detector || {}).length === 0 ? (
                                    <span className="text-ink-soft">—</span>
                                ) : Object.entries(status.by_detector).map(([d, n]) => (
                                    <div key={d}>{d}: <span className="text-stamp-red">{n}</span></div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Run button */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                        <button
                            onClick={runSweep}
                            disabled={busy}
                            data-testid="admin-contacts-audit-run"
                            className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {busy ? "Sweeping…" : "Run sweep now"}
                        </button>
                        <button
                            onClick={load}
                            disabled={busy}
                            data-testid="admin-contacts-audit-refresh"
                            className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                        >
                            <RefreshCw className="w-3 h-3" /> Refresh
                        </button>
                        {last && (
                            <span className="font-body text-[11px] text-ink-soft" data-testid="admin-contacts-audit-last">
                                last: {last.proposals.length} filed · {Object.keys(last.by_detector).length || 0} detector(s)
                            </span>
                        )}
                    </div>

                    {/* History timeline */}
                    <div className="border-t border-ink/20 pt-3" data-testid="admin-contacts-audit-history">
                        <div className="label-typed text-[10px] mb-2">Sweep timeline · last {history.length}</div>
                        {history.length === 0 ? (
                            <div className="font-body text-xs text-ink-soft">No sweeps recorded yet — autonomous loop runs every {status?.interval_hours}h, or fire one manually above.</div>
                        ) : (
                            <ul className="space-y-1 font-body text-[11px]">
                                {history.map((r) => (
                                    <li
                                        key={r.id}
                                        className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-b border-ink/10 pb-1 last:border-b-0"
                                        data-testid={`admin-contacts-audit-row-${r.id}`}
                                    >
                                        <span className="font-heading text-ink tracking-wide">{formatTime(r.ran_at)}</span>
                                        <span className={`uppercase tracking-[0.18em] text-[9px] px-1.5 py-0.5 border ${
                                            r.source === "autonomous"
                                                ? "border-stamp-red text-stamp-red"
                                                : "border-ink/40 text-ink"
                                        }`}>
                                            {r.source}
                                        </span>
                                        <span className="text-ink">
                                            {r.proposals_filed} filed
                                        </span>
                                        {Object.keys(r.by_detector || {}).length > 0 && (
                                            <span className="text-ink-soft">
                                                · {Object.entries(r.by_detector).map(([d, n]) => `${d}=${n}`).join(" · ")}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}


function PhotoTaskTodoistPanel() {
    // Iter 31.118 (30 May 2026) — combined Photo Task + Todoist health strip.
    // Greenlit by Six after Phase B went live. Three rows of state:
    //   1. Photo task pipeline (rooms tracked, open tasks, queued microbriefs)
    //   2. Todoist sync (token state, project pin, live ping)
    //   3. Calendar feeds (count + latest sync result)
    const [photoStatus, setPhotoStatus] = useState(null);
    const [todoistStatus, setTodoistStatus] = useState(null);
    const [calendarStatus, setCalendarStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const load = async () => {
        setErr("");
        try {
            const [p, t, c] = await Promise.all([
                api.get("/photo-task/status"),
                api.get("/todoist/status"),
                api.get("/calendar/feeds/status"),
            ]);
            setPhotoStatus(p.data);
            setTodoistStatus(t.data);
            setCalendarStatus(c.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    return (
        <div className="paper-card border border-dashed border-ink/30 p-4 mb-4" data-testid="admin-photo-todoist-calendar">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink">
                    Photo Task · Todoist · Calendar
                </h3>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="admin-photo-todoist-refresh"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red disabled:opacity-40 inline-flex items-center gap-1"
                >
                    <RefreshCw className="w-3 h-3" /> Refresh
                </button>
            </div>

            {loading ? (
                <div className="font-body text-xs text-ink-soft">Loading…</div>
            ) : err ? (
                <div className="text-stamp-red font-body text-xs" data-testid="admin-photo-todoist-error">
                    Error: {err}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Photo task */}
                    <div className="border-l-2 border-stamp-red pl-3" data-testid="admin-strip-photo">
                        <div className="label-typed text-[10px] mb-1">Photo Task</div>
                        <div className="font-body text-xs space-y-0.5">
                            <div>Pipeline: <span className={photoStatus?.enabled ? "text-stamp-red font-heading" : "text-ink-soft"}>
                                {photoStatus?.enabled ? "ON" : "off"}
                            </span></div>
                            <div>Model: <span className="text-ink">{photoStatus?.model}</span></div>
                            <div>Cooldown: <span className="text-ink">{photoStatus?.cooldown_seconds}s</span></div>
                            <div>Rooms tracked: <span className="text-ink">{photoStatus?.rooms_tracked ?? 0}</span></div>
                            <div>Open tasks: <span className="text-ink">{photoStatus?.open_tasks_total ?? 0}</span></div>
                            <div>Microbriefs queued: <span className="text-ink">{photoStatus?.microbriefs_queued ?? 0}</span></div>
                        </div>
                    </div>

                    {/* Todoist */}
                    <div className="border-l-2 border-stamp-red pl-3" data-testid="admin-strip-todoist">
                        <div className="label-typed text-[10px] mb-1">Todoist sync</div>
                        <div className="font-body text-xs space-y-0.5">
                            <div>Configured:{" "}
                                <span className={todoistStatus?.configured ? "text-stamp-red font-heading" : "text-ink-soft"}>
                                    {todoistStatus?.configured ? "YES" : "no"}
                                </span>
                            </div>
                            {todoistStatus?.configured && (
                                <>
                                    <div>Live ping:{" "}
                                        <span className={todoistStatus?.live_ok ? "text-stamp-red font-heading" : "text-stamp-red"}>
                                            {todoistStatus?.live_ok ? "OK" : "FAIL"}
                                        </span>
                                    </div>
                                    <div>Projects: <span className="text-ink">{todoistStatus?.projects_count ?? 0}</span></div>
                                    <div>Token tail: <span className="text-ink font-mono">{todoistStatus?.token_tail || "—"}</span></div>
                                </>
                            )}
                            {!todoistStatus?.configured && (
                                <div className="text-ink-soft italic">PUT /api/todoist/credentials to wire</div>
                            )}
                        </div>
                    </div>

                    {/* Calendar */}
                    <div className="border-l-2 border-stamp-red pl-3" data-testid="admin-strip-calendar">
                        <div className="label-typed text-[10px] mb-1">Calendar feeds</div>
                        <div className="font-body text-xs space-y-0.5">
                            <div>Autonomous: <span className={calendarStatus?.autonomous_enabled ? "text-stamp-red font-heading" : "text-ink-soft"}>
                                {calendarStatus?.autonomous_enabled ? "ON" : "off"}
                            </span></div>
                            <div>Loop: <span className={calendarStatus?.loop_running ? "text-stamp-red font-heading" : "text-ink-soft"}>
                                {calendarStatus?.loop_running ? "running" : "paused"}
                            </span></div>
                            <div>Interval: <span className="text-ink">{Math.round((calendarStatus?.interval_seconds || 0) / 60)} min</span></div>
                            <div>Feeds registered: <span className="text-ink">{calendarStatus?.feeds_registered ?? 0}</span></div>
                            <div>Events filed: <span className="text-ink font-heading">{calendarStatus?.calendar_events_filed ?? 0}</span></div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


export default function Admin() {
    return (
        <Shell>
            <StampHeader
                kicker="Owner-only · idempotent operations"
                title="Admin"
                sub="Village Control Centre. Single page Six can scan in the morning, fire a normalise, fire a backfill, see what's currently sticky. The per-page buttons stay where they are — this is the canonical home for the same operations plus the maintenance ones that don't deserve their own page."
            />

            <BuildBadge variant="card" />
            {/* Iter 31.215 — agent-curated corner stamp under build badge.
                Internal-only surface; operatives never see this. */}
            <EasterEggSlot
                path="admin"
                slot="admin.corner_stamp"
                as="div"
                className="text-right text-[10px] font-body italic text-ink-soft opacity-60 -mt-2 mb-4 tracking-[0.14em]"
            />

            <HealthPanel />

            <AgentActivityPanel />

            <AlertHistoryPanel />

            <KeysManifestPanel />

            <AgentRegistryPanel />

            <ContentBlocksPanel />

            <MultiAgentTokensPanel />

            <RadioPoolPanel />

            <StationPublisherPanel />

            <MurekaExportPanel />

            <LinkedInContactsPanel />

            <TestModeStatusBanner />

            <MCPStatusPanel />

            <PairingDiagnostic />

            <MurekaLibraryTile />

            <AddressesPanel />

            <div className="paper-card border border-dashed border-ink/30 p-4 mb-4" data-testid="admin-mail-link">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <div>
                        <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink">
                            Agent Mail · inbound + outbound
                        </h3>
                        <p className="font-body text-xs text-ink-soft mt-1 max-w-2xl">
                            Per-agent thread of email at <code>*@spyfinance.cloud</code>.
                            Reply via Resend so each alias becomes two-way (iter 31.168).
                        </p>
                    </div>
                    <a
                        href="/mail"
                        data-testid="admin-mail-open"
                        className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper"
                    >
                        Open Mail →
                    </a>
                </div>
            </div>

            <div className="mb-4 flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-stamp-red" />
                <h2 className="font-heading uppercase tracking-[0.22em] text-sm text-ink">
                    Maintenance
                </h2>
            </div>

            <ContactsAuditPanel />
            <PhotoTaskTodoistPanel />
            <WelcomeCreditsPanel />
            <SiteSnapshotPanel />
            <PreWarmPanel />
            <AgentTokenPanel />
            <GrantToEmailPanel />
            <CommsNormalisePanel />
            <RadioBrandPanel />

            <div className="mt-10 hairline" />
            <div className="mt-4 label-typed text-center">
                Be seeing you · run early, run often · originals never altered
            </div>
        </Shell>
    );
}
